import { useSuspenseCurrentMedication } from '@/hooks/useCurrentMedication'
import { useMedicationOptions } from '@/hooks/useMedicationOptions'
import { usePreviousOrderDates } from '@/hooks/usePreviousOrderDates'
import { useToast } from '@/hooks/use-toast'
import { isMultiProductOrderTreatmentComplete } from '@/lib/api/med-requests/multi-product-order/family-actions'
import {
  decidableMultiProductOrderTreatmentIds,
  isInClinicalReview,
  multiProductOrderTreatmentRequestsOf
} from '@/lib/med-requests'
import { afterApprovalStatuses, afterRejectionStatuses } from '@/lib/constants/med-requests'
import { buildMedicationOptions, takenMedicationValues, toMedicationOption } from '@/lib/medication-options'
import {
  type MultiProductOrderTreatment,
  selectIsDecisionSubmitting,
  type TreatmentDecision,
  useMedicationRequestStore
} from '@/lib/stores/medication-request'
import { cn, getOrderStatusBadgeClasses, isFutureIssueDate } from '@/lib/utils'
import type { MedRequest, Medication } from '@/types/data'
import { format } from 'date-fns'
import { RotateCcw } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import TreatmentCard, { IssueDateField } from '../treatment-card'

type TreatmentView = {
  /** The treatment's own med request id — also the Clinical decision panel's target. */
  id: string
  medication: Medication | null
  direction: string
  /** Shown instead of the actions: its recorded or actioned decision, else a non-decidable stage. */
  statusLabel: string | null
  /** The decision is only recorded on the card, so it can be undone. */
  isRecorded: boolean
  /** Stage of a decidable treatment past clinical review (e.g. Approved), shown beside its actions. */
  decidableStage: string | null
  onMedicationChange: (medication: Medication, direction: string) => void
  onDirectionChange: (direction: string) => void
}

const RECORDED_DECISION_LABELS: Record<TreatmentDecision['action'], string> = {
  approve: 'Approved',
  reject: 'Rejected'
}

// 'On Hold' is in afterApprovalStatuses but still undecided, so it shows its stage.
const ON_HOLD_STAGE = 'on hold'

const stageDecisionLabelOf = (stage: string | null): string | null => {
  const normalized = stage?.toLowerCase() ?? ''
  if (normalized === ON_HOLD_STAGE) return null
  if (afterApprovalStatuses.some(entry => entry.toLowerCase() === normalized)) return 'Approved'
  if (afterRejectionStatuses.some(entry => entry.toLowerCase() === normalized)) return 'Rejected'
  return null
}

type DecisionActionsProps = {
  position: number
  /** Badge shown instead of the actions. */
  statusLabel: string | null
  decidableStage: string | null
  /** Set while the decision is only recorded on the card: its Undo drops it. */
  onUndo?: () => void
  isEditable: boolean
  /** False while the card is missing a medication or directions. */
  canApprove: boolean
  /** The decision the Clinical decision panel is open for on this card, if any. */
  activeAction: TreatmentDecision['action'] | null
  onDecide: (action: TreatmentDecision['action']) => void
}

const ACTIVE_ACTION_CLASSES = 'ring-2 ring-[#153CB7] ring-offset-2'

function TreatmentDecisionActions({
  position,
  statusLabel,
  decidableStage,
  onUndo,
  isEditable,
  canApprove,
  activeAction,
  onDecide
}: DecisionActionsProps) {
  if (!statusLabel) {
    return (
      <div className='flex items-center gap-4'>
        {decidableStage && (
          <span
            data-testid={`treatment-stage-${position}`}
            className={cn(
              'flex h-8 items-center rounded-full border px-3 text-xs font-semibold leading-[170%]',
              getOrderStatusBadgeClasses(decidableStage)
            )}
          >
            {decidableStage}
          </span>
        )}
        <button
          type='button'
          data-testid={`treatment-approve-${position}`}
          aria-pressed={activeAction === 'approve'}
          disabled={!isEditable || !canApprove}
          onClick={() => onDecide('approve')}
          className={cn(
            'flex w-[168px] h-14 items-center justify-center rounded-2xl bg-[#153CB7] px-6 text-white text-sm font-semibold leading-[170%] hover:bg-[#0F2E8A] transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
            activeAction === 'approve' && ACTIVE_ACTION_CLASSES
          )}
        >
          Approve
        </button>
        <button
          type='button'
          data-testid={`treatment-reject-${position}`}
          aria-pressed={activeAction === 'reject'}
          disabled={!isEditable}
          onClick={() => onDecide('reject')}
          className={cn(
            'flex w-[168px] h-14 items-center justify-center rounded-2xl border border-custom-neutral-500 bg-white px-6 text-[#153CB7] text-sm font-semibold leading-[170%] hover:bg-custom-neutral-200 transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
            activeAction === 'reject' && ACTIVE_ACTION_CLASSES
          )}
        >
          Reject
        </button>
      </div>
    )
  }

  const badge = (
    <span
      data-testid={`treatment-status-${position}`}
      className={cn(
        'flex w-[168px] h-14 items-center justify-center rounded-2xl border text-sm font-semibold leading-[170%]',
        getOrderStatusBadgeClasses(statusLabel)
      )}
    >
      {statusLabel}
    </span>
  )

  if (!onUndo) return badge

  return (
    <div className='flex items-center gap-4'>
      {badge}
      <button
        type='button'
        data-testid={`treatment-undo-${position}`}
        disabled={!isEditable}
        onClick={onUndo}
        className='flex items-center gap-2 text-[#153CB7] text-sm font-semibold leading-[170%] hover:text-[#0F2E8A] transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
      >
        <RotateCcw className='size-5 shrink-0' strokeWidth={2.5} aria-hidden />
        Undo
      </button>
    </div>
  )
}

/**
 * One card per treatment. Approve / Reject open the Clinical decision panel, which records the
 * decision on the card until Submit decision sends it. Treatment 1 stays on the flat store fields;
 * the rest live in `multiProductOrderTreatments`. One issue date below the cards covers them all.
 */
export default function MultiProductOrderPrescriptionDetails({ isEditable }: { isEditable: boolean }) {
  const {
    medicationIssueDate,
    setMedicationIssueDate,
    medicationDirection,
    setMedicationDirection,
    medication,
    setMedication,
    multiProductOrderTreatments,
    updateMultiProductOrderTreatment,
    multiProductOrderSeededRequestId,
    seedMultiProductOrderTreatments,
    decisionPanelTarget,
    setDecisionPanelTarget,
    treatmentDecisions,
    undoTreatmentDecision,
    setSidebarView,
    setRejectionReason
  } = useMedicationRequestStore()
  // While a decision is being sent, cards can't record another or change fields its payloads already captured.
  const isDecisionSubmitting = useMedicationRequestStore(selectIsDecisionSubmitting)

  const { toast } = useToast()
  const { data: medRequestData } = useSuspenseCurrentMedication()
  const { catalogue, medications, findMedication } = useMedicationOptions()
  const { advancedPreviousOrderDate, lastIssuedDate } = usePreviousOrderDates()

  const treatmentRequests = useMemo(() => multiProductOrderTreatmentRequestsOf(medRequestData), [medRequestData])

  const isFutureDate = isFutureIssueDate(medicationIssueDate)

  // Seed every card once per request; the store guard keeps refetches from clobbering edits.
  useEffect(() => {
    if (!medications?.length) return
    if (multiProductOrderSeededRequestId === medRequestData.id) return

    const toTreatment = (row: MedRequest): MultiProductOrderTreatment => {
      const rowMedication = row.orderedItem?.id ? (findMedication(row.orderedItem.id) ?? null) : null
      return {
        id: row.id,
        medication: rowMedication,
        direction: row.orderedItem?.directions || rowMedication?.medicationDirections || ''
      }
    }

    const [primary, ...extras] = treatmentRequests.map(toTreatment)
    setMedication(primary.medication)
    setMedicationDirection(primary.direction)
    seedMultiProductOrderTreatments(medRequestData.id, extras)
  }, [
    medications,
    medRequestData.id,
    multiProductOrderSeededRequestId,
    treatmentRequests,
    seedMultiProductOrderTreatments,
    setMedication,
    setMedicationDirection,
    findMedication
  ])

  // A recorded decision only stands while its treatment is still decidable.
  const decidableIds = new Set(decidableMultiProductOrderTreatmentIds(medRequestData))

  // Decidable: Approve / Reject, plus the stage once past clinical review. Otherwise its decision or stage.
  const stageViewOf = (treatmentId: string, stage: string | null = null) => {
    if (!decidableIds.has(treatmentId)) {
      return { statusLabel: stageDecisionLabelOf(stage) ?? stage, isRecorded: false, decidableStage: null }
    }

    const recorded = treatmentDecisions[treatmentId]
    return {
      statusLabel: recorded ? RECORDED_DECISION_LABELS[recorded.action] : null,
      isRecorded: !!recorded,
      decidableStage: isInClinicalReview({ stage }) ? null : stage
    }
  }
  const stagesById = new Map(treatmentRequests.map(row => [row.id, row.stage]))

  const treatments: TreatmentView[] = [
    {
      id: medRequestData.id,
      medication,
      direction: medicationDirection,
      ...stageViewOf(medRequestData.id, medRequestData.stage),
      onMedicationChange: (nextMedication, nextDirection) => {
        setMedication(nextMedication)
        setMedicationDirection(nextDirection)
      },
      onDirectionChange: setMedicationDirection
    },
    ...multiProductOrderTreatments.map<TreatmentView>(treatment => ({
      id: treatment.id,
      medication: treatment.medication,
      direction: treatment.direction,
      ...stageViewOf(treatment.id, stagesById.get(treatment.id)),
      onMedicationChange: (nextMedication, nextDirection) =>
        updateMultiProductOrderTreatment(treatment.id, { medication: nextMedication, direction: nextDirection }),
      onDirectionChange: direction => updateMultiProductOrderTreatment(treatment.id, { direction })
    }))
  ]

  // Locked while a recorded approval would go out with it, or once no card is left to decide.
  const hasRecordedApproval = [...decidableIds].some(id => treatmentDecisions[id]?.action === 'approve')
  const isIssueDateEditable =
    isEditable && !isDecisionSubmitting && !hasRecordedApproval && treatments.some(treatment => !treatment.statusLabel)

  // Each card uses its own item's cadence; the order's items may differ.
  const catalogueOptionsById = useMemo(
    () =>
      new Map(
        treatmentRequests.map(row => [
          row.id,
          buildMedicationOptions({
            medications: catalogue,
            orderedItemId: row.orderedItem?.id,
            requestFrequency: row.subscriptionFrequency,
            orderNumber: row.orderNumber
          })
        ])
      ),
    [catalogue, treatmentRequests]
  )

  // A product-level pick has no catalogue option; it's prepended so the combobox can label it.
  const cardOptionsFor = (treatment: TreatmentView) => {
    const cardOptions = catalogueOptionsById.get(treatment.id) ?? []
    const pick = treatment.medication
    return pick && !cardOptions.some(option => option.value === pick.wooSKU)
      ? [toMedicationOption(pick), ...cardOptions]
      : cardOptions
  }

  // A medication picked on one card is disabled on the others, decided cards included.
  const disabledValuesFor = (current: TreatmentView) =>
    takenMedicationValues(
      treatments.filter(entry => entry.id !== current.id).map(entry => entry.medication),
      medications ?? []
    )

  const handleMedicationChange = (treatment: TreatmentView) => (lookupId: string) => {
    const medicationData = findMedication(lookupId)
    if (!medicationData) throw new Error('Medication not found')

    // OTC items carry no directions, so clear rather than seed from the catalogue.
    const nextDirection = medicationData.type === 'OTC' ? '' : (medicationData.medicationDirections ?? '')
    treatment.onMedicationChange(medicationData, nextDirection)
  }

  const handleSetAdvancedDate = () => {
    if (!advancedPreviousOrderDate) return

    setMedicationIssueDate(advancedPreviousOrderDate)
    toast({
      title: 'Issue date set',
      description: `Issue date set to [${format(advancedPreviousOrderDate, 'dd MMMM yyyy')}]`
    })
  }

  return (
    <div data-testid='prescription-details'>
      <div className='rounded-2xl border border-custom-neutral-200 p-6 shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'>
        <p
          id='prescription-details'
          className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] font-feature-settings-liga-off font-feature-settings-clig-off mb-6'
        >
          Prescription Details
        </p>

        <div className='flex flex-col gap-6'>
          {treatments.map((treatment, index) => {
            const isCardEditable = isEditable && !isDecisionSubmitting && !treatment.statusLabel

            return (
              <TreatmentCard
                key={treatment.id}
                index={index}
                isEditable={isCardEditable}
                options={cardOptionsFor(treatment)}
                disabledValues={disabledValuesFor(treatment)}
                medication={treatment.medication}
                direction={treatment.direction}
                onMedicationChange={handleMedicationChange(treatment)}
                onDirectionChange={treatment.onDirectionChange}
                headerAction={
                  <TreatmentDecisionActions
                    position={index + 1}
                    statusLabel={treatment.statusLabel}
                    decidableStage={treatment.decidableStage}
                    onUndo={treatment.isRecorded ? () => undoTreatmentDecision(treatment.id) : undefined}
                    isEditable={isEditable && !isDecisionSubmitting}
                    canApprove={isMultiProductOrderTreatmentComplete(treatment)}
                    activeAction={decisionPanelTarget?.treatmentId === treatment.id ? decisionPanelTarget.action : null}
                    onDecide={action => {
                      // The panel's rejection views share the sidebar's message, so it starts empty.
                      setRejectionReason('')
                      setSidebarView('navigation-menu')
                      setDecisionPanelTarget({ treatmentId: treatment.id, action })
                    }}
                  />
                }
              />
            )
          })}
        </div>

        <IssueDateField
          isEditable={isIssueDateEditable}
          issueDate={medicationIssueDate}
          onIssueDateChange={setMedicationIssueDate}
          lastIssuedDate={lastIssuedDate}
          onSetAdvancedDate={advancedPreviousOrderDate ? handleSetAdvancedDate : undefined}
        />
      </div>

      {isFutureDate && (
        <div className='rounded-2xl mt-4 border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'>
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            This request is scheduled for approval on <b>{format(medicationIssueDate, 'MMMM d, yyyy')}.</b>
          </p>
        </div>
      )}
    </div>
  )
}
